import { createContext, useContext } from 'react';
import type { User, Session } from '@supabase/supabase-js';

/**
 * REK-1985 (SSO.4): the auth context contract, shared byte-for-byte between
 * the Supabase provider (src/providers/AuthProvider.tsx) and the Clerk
 * provider (src/providers/ClerkAuthProvider.tsx). Consumers only ever see
 * this shape — which engine fills it is decided by VITE_AUTH_PROVIDER.
 */

export type OnboardingStatus = 'not_started' | 'in_progress' | 'completed' | null;

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  onboardingStatus: OnboardingStatus;
  accountLockedError: string | null;
  setAccountLockedError: (error: string | null) => void;
  // REK-1754: distinct from accountLockedError. Set when the lock check itself
  // could NOT be completed (DB error / exception) and we fail CLOSED by signing
  // the session out. It carries a NEUTRAL retry message — NOT the permanent
  // "account locked, contact support" copy — so a legitimate user who hit a
  // transient blip isn't wrongly told their account is locked.
  sessionError: string | null;
  setSessionError: (error: string | null) => void;
  signOut: () => Promise<void>;
  handleSessionError: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// REK-1754: shared copy. The locked message is matched by string elsewhere
// (LemonadeLogin keys the "contact support" render off `support@reklaimyou.com`),
// so keep the substring stable. The session-verify message MUST NOT contain
// that substring — it is a transient/retry state, not a permanent lock.
export const ACCOUNT_LOCKED_MESSAGE =
  'Your account has been locked. Please contact support@reklaimyou.com for assistance.';
export const SESSION_VERIFY_FAILED_MESSAGE =
  "We couldn't verify your account right now. Please sign in again.";
// REK-2030: the Clerk session is valid but no legacy Reklaim profile is linked
// to it, and the account is too old for the SSO.2 webhook race to explain the
// gap — no provisioning is coming, so say so instead of spinning for 90s. Like
// SESSION_VERIFY_FAILED_MESSAGE this MUST NOT contain `support@reklaimyou.com`,
// or LemonadeLogin renders it as a permanent account lock.
export const ACCOUNT_NOT_PROVISIONED_MESSAGE =
  "This account isn't set up for the Reklaim app yet. Please sign in with the email or phone number you originally registered with, or use the help chat if you think this is a mistake.";

export function useAuthContext(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
